import { createRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { authedLayoutRoute } from './authed.js';
import { ApiError } from '../api/client.js';
import { downloadTenantExport, isWrongPassword } from '../api/export.js';
import { Alert } from '../components/Alert.js';

/**
 * /data-export — the tenant's own data export (ADR-0034, Retention Phase 2a).
 * Owner/admin. The FE half of `POST /v1/export/me`.
 *
 * ★ A PAGE, NOT A BUTTON IN A MODAL — deliberately (reviewed 2026-09-25).
 *
 * The honest content of this feature is its BOUNDARY: what leaves the building
 * and what does not. One click here produces every employee's identity, salary,
 * bank details and tax identifiers in a single file. So the page states what the
 * bundle contains, and — at equal weight — what it deliberately excludes, ABOVE
 * the password field. The information order is consequence first, action last:
 * read what is about to be extracted, then authenticate to extract it. A modal
 * would put the button first and hide exactly the part that needs reading.
 *
 * The nav link is owner/admin-only (`AuthedLayout.adminNavGroups`), but that is
 * convenience-in-depth: **the API's `requireRole('owner','admin')` is the wall**
 * (pinned by EXP-GATE-1). Hiding a link protects nobody on its own.
 *
 * Re-auth is per export, never cached. This page holds no "recently
 * re-authenticated" flag and no export token: every download sends the password
 * again, because every download is another full extraction. The field is cleared
 * on success so the next one cannot be a stray Enter keypress.
 */
export const dataExportRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/data-export',
  component: DataExportPage,
});

/**
 * Mirrors PRODUCT_MANUAL §B-3.2. Kept here as plain copy rather than fetched:
 * it describes a decision (the allow-list), not runtime state.
 */
const INCLUDED = [
  'Employees, compensation and pay components',
  'Payslips and payroll runs — with the rule-spec hash and interpreter version each was calculated with',
  'Leave policies, requests and balances',
  'Attendance, work patterns and timesheets',
  'Performance cycles, goals and outcomes',
  'Loans and their deduction ledgers, and expense claims',
  'Assets and their custody history',
  'Document records — name, type, size and date',
  'Invoices, subscription and billing periods, entitlements, bank profile',
];

const EXCLUDED: { what: string; why: string }[] = [
  {
    what: 'Passwords, session tokens and password-reset tokens',
    why: 'Credentials, not data. Including them would make a portability feature a security defect.',
  },
  {
    what: 'The files behind your documents — receipts, CVs, handover documents, payslip PDFs',
    why: 'The export carries each document’s record and its id, not the bytes; a JSON file cannot hold binaries. Download each file from its own page, or a payslip PDF from its payroll run.',
  },
  {
    what: 'Internal storage keys and artifact references',
    why: 'Opaque internal handles. They identify nothing you can use.',
  },
  {
    what: 'Payment-provider references and void reasons',
    why: 'Operator-internal billing plumbing. This file shows exactly what your Billing & plan page shows, no more.',
  },
  {
    what: 'The audit log, and your user accounts',
    why: 'Records about your use of LumoHR rather than your HR data — and exporting user rows would mean exporting password hashes with them.',
  },
];

function DataExportPage(): JSX.Element {
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [downloaded, setDownloaded] = useState<string | null>(null);

  const exportMutation = useMutation({
    mutationFn: () => downloadTenantExport(password),
    onSuccess: ({ blob, filename }) => {
      // Same save pattern as /me/documents — a hidden anchor, revoked on the
      // next tick. Nothing is cached: the Blob is dropped with the object URL.
      const url = URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = filename || 'lumohr-export.json';
      window.document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1_000);
      setDownloaded(filename || 'lumohr-export.json');
      // Clear it: the next export must be a deliberate act, not a stray Enter.
      setPassword('');
    },
    onError: (err: unknown) => {
      setDownloaded(null);
      if (err instanceof ApiError && err.status === 401) {
        // Two different 401s, two different sentences. Telling someone their
        // password is wrong when their SESSION expired sends them to reset a
        // password that was never the problem.
        setError(
          isWrongPassword(err.body)
            ? err.message
            : 'Your session has expired. Sign in again, then retry the export.',
        );
        return;
      }
      if (err instanceof ApiError && err.status === 403) {
        setError('Only an owner or admin can export the organisation’s data.');
        return;
      }
      setError(err instanceof ApiError ? err.message : 'Could not generate the export. Try again.');
    },
  });

  const canSubmit = password.length > 0 && !exportMutation.isPending;

  return (
    <div className="mx-auto max-w-3xl px-6 py-8 space-y-8">
      <header className="space-y-2">
        <h1 className="font-serif text-2xl text-white">Export your data</h1>
        <p className="text-sm text-gray-400">
          A single JSON file containing your organisation’s records, for you to keep. This is the
          export named in our{' '}
          <a href="/legal/data-retention" className="underline hover:text-white">
            Data Retention &amp; Deletion Policy
          </a>{' '}
          — you can take a copy before anything is deleted.
        </p>
      </header>

      <Alert variant="warning" title="Read this before you download">
        This file contains <strong>every employee’s identity, salary, bank details and tax
        identifiers</strong> in one place. It is the most sensitive thing the product can produce.
        Once it is on your computer, protecting it is yours to do — and anyone you send it to has all
        of it. We keep no copy: the file is built for this one request and stored nowhere, so
        downloading it again means asking again.
      </Alert>

      <section className="space-y-3">
        <h2 className="font-serif text-lg text-white">What’s in it</h2>
        <ul className="list-disc space-y-1 pl-5 text-sm text-gray-300">
          {INCLUDED.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </section>

      <section className="space-y-3">
        <h2 className="font-serif text-lg text-white">What’s deliberately not in it</h2>
        <dl className="space-y-3 text-sm">
          {EXCLUDED.map(({ what, why }) => (
            <div key={what} className="rounded border border-gray-800 bg-gray-900/40 p-3">
              <dt className="text-gray-200">{what}</dt>
              <dd className="mt-1 text-gray-400">{why}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="space-y-3 border-t border-gray-800 pt-6">
        <h2 className="font-serif text-lg text-white">Confirm it’s you</h2>
        <p className="text-sm text-gray-400">
          Enter your password again. Being signed in is not enough for this one action, and each
          export is recorded in the audit log with your name and the time.
        </p>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (!canSubmit) return;
            setError(null);
            setDownloaded(null);
            exportMutation.mutate();
          }}
        >
          <label className="block text-sm text-gray-300 space-y-1">
            <span>Your password</span>
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              aria-label="Your password"
              className="w-full max-w-sm rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm"
            />
          </label>
          {error ? <Alert variant="error">{error}</Alert> : null}
          {downloaded ? (
            <Alert variant="success" title="Export downloaded">
              Saved as <strong>{downloaded}</strong>. We kept no copy — to get it again, enter your
              password and download a new one.
            </Alert>
          ) : null}
          <button
            type="submit"
            disabled={!canSubmit}
            className="rounded bg-white text-black px-4 py-2 text-sm font-medium hover:bg-gray-200 disabled:opacity-40"
          >
            {exportMutation.isPending ? 'Preparing your export…' : 'Download export'}
          </button>
        </form>
      </section>
    </div>
  );
}
